import React, { useContext, useState } from 'react';
import SEO from '../components/SEO';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import AuthContext from '../context/AuthContext';

const FeedbackPage = () => {
  const { authTokens, backendUrl } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    feedback_type: 'feedback',
    text: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.text.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter your feedback',
        severity: 'error'
      });
      return;
    }

    if (formData.text.length > 500) {
      setSnackbar({
        open: true,
        message: 'Feedback must be 500 characters or less',
        severity: 'error'
      });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${backendUrl}api/feedback/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authTokens.access}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Thank you for your feedback!',
          severity: 'success'
        });
        // Reset form
        setFormData({
          feedback_type: 'feedback',
          text: ''
        });
      } else {
        setSnackbar({
          open: true,
          message: data.error || 'Failed to submit feedback',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSnackbar({
        open: true,
        message: 'An error occurred while submitting feedback',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <>
    <SEO title="Feedback - Couplendar" />
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Card>
        <CardContent>
          <Typography variant="h4" component="h1" gutterBottom>
            Share Your Feedback
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            We'd love to hear from you! Share your feedback on how we can improve Couplendar,
            or suggest new ideas for features you'd like to see.
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="feedback-type-label">Type</InputLabel>
              <Select
                labelId="feedback-type-label"
                id="feedback_type"
                name="feedback_type"
                value={formData.feedback_type}
                label="Type"
                onChange={handleChange}
              >
                <MenuItem value="feedback">Feedback</MenuItem>
                <MenuItem value="new_ideas">New Ideas</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={6}
              label="Your Message"
              name="text"
              value={formData.text}
              onChange={handleChange}
              placeholder="Tell us what you think..."
              helperText={`${formData.text.length}/500 characters`}
              inputProps={{ maxLength: 500 }}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
              sx={{ height: 48 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Submit Feedback'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
    </>
  );
};

export default FeedbackPage;
